import React, { useState, useEffect } from 'react';
import { getHistoryConfig, setHistoryConfig } from '../../utils/api';

// Frequency options (in seconds)
const FREQUENCY_OPTIONS = [
  { value: 5, label: '5 segundos' },
  { value: 10, label: '10 segundos' },
  { value: 15, label: '15 segundos' },
  { value: 30, label: '30 segundos' },
  { value: 60, label: '1 minuto' },
  { value: 120, label: '2 minutos' },
  { value: 300, label: '5 minutos' },
  { value: 600, label: '10 minutos' },
  { value: 900, label: '15 minutos' },
  { value: 1800, label: '30 minutos' },
];

/**
 * History Configuration Panel Component
 */
export const HistoryConfigPanel = ({ isOpen, onClose }) => {
  const [persistHistory, setPersistHistory] = useState(false);
  const [historyFrequency, setHistoryFrequency] = useState(30);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalConfig, setOriginalConfig] = useState({ persistHistory: false, historyFrequency: 30 });

  // Load config when panel opens
  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    const loadConfig = async () => {
      setLoading(true);
      try {
        const config = await getHistoryConfig();
        if (!mounted) return;
        setPersistHistory(config.persistHistory);
        setHistoryFrequency(config.historyFrequency);
        setOriginalConfig({ persistHistory: config.persistHistory, historyFrequency: config.historyFrequency });
        setHasChanges(false);
      } catch (err) {
        console.error('Error loading history config:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadConfig();
    return () => { mounted = false; };
  }, [isOpen]);

  // Track changes
  useEffect(() => {
    const changed = persistHistory !== originalConfig.persistHistory ||
                    historyFrequency !== originalConfig.historyFrequency;
    setHasChanges(changed);
  }, [persistHistory, historyFrequency, originalConfig]);

  // Handle save
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await setHistoryConfig(persistHistory, historyFrequency);
      if (result.ok) {
        setOriginalConfig({ persistHistory: result.persistHistory, historyFrequency: result.historyFrequency });
        setHasChanges(false);
        onClose();
      }
    } catch (err) {
      console.error('Error saving history config:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle with animation
  const handleToggle = () => {
    setPersistHistory(!persistHistory);
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} />
      <div className={`history-config-panel ${isOpen ? 'active' : ''}`}>
        <div className="config-header">
          <div className="config-title">
            <span>📊</span>
            Configuracion Historico
          </div>
          <button className="sidebar-close" onClick={onClose}>×</button>
        </div>

        <div className="config-content">
          {loading ? (
            <div className="history-config-loading">
              <div className="historical-spinner"></div>
              <span>Cargando configuracion...</span>
            </div>
          ) : (
            <>
              {/* Activation Toggle */}
              <div className="config-card">
                <div className="config-card-header">
                  <span className="config-card-icon">💾</span>
                  <span className="config-card-title">Guardar Datos</span>
                </div>
                <div className="history-toggle-container">
                  <div className="history-toggle-info">
                    <span className="history-toggle-label">
                      {persistHistory ? 'Guardado activo' : 'Guardado inactivo'}
                    </span>
                    <span className="history-toggle-description">
                      {persistHistory
                        ? 'Los datos de sensores se estan guardando en el historico'
                        : 'Los datos de sensores no se estan guardando'}
                    </span>
                  </div>
                  <button
                    className={`history-toggle-switch ${persistHistory ? 'active' : ''}`}
                    onClick={handleToggle}
                    aria-label="Toggle history saving"
                  >
                    <span className="history-toggle-slider"></span>
                  </button>
                </div>
              </div>

              {/* Frequency Selector */}
              <div className="config-card">
                <div className="config-card-header">
                  <span className="config-card-icon">⏱️</span>
                  <span className="config-card-title">Frecuencia de Guardado</span>
                </div>
                <div className="config-field">
                  <label className="config-label">Guardar datos cada</label>
                  <select
                    className="config-select history-frequency-select"
                    value={historyFrequency}
                    onChange={(e) => setHistoryFrequency(Number(e.target.value))}
                    disabled={!persistHistory}
                  >
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span className="config-hint">
                    {persistHistory
                      ? `Se guardaran aproximadamente ${Math.round(86400 / historyFrequency)} registros por dia por sensor`
                      : 'Activa el guardado para configurar la frecuencia'}
                  </span>
                </div>
              </div>

              {/* Info Card */}
              <div className="history-info-card">
                <div className="history-info-icon">ℹ️</div>
                <div className="history-info-text">
                  Una frecuencia mas alta genera mas datos pero permite un analisis mas detallado.
                  Considera el espacio de almacenamiento disponible.
                </div>
              </div>

              {/* Save Button */}
              <button
                className={`config-save-btn ${!hasChanges ? 'disabled' : ''}`}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Guardando...' : 'Guardar Configuracion'}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
